/**
 * NotificationManagement Component
 * 
 * Comprehensive notification management system for the admin dashboard.
 * Follows the same patterns as NewsManagement for consistency.
 * 
 * Features:
 * - Create, edit, delete notifications
 * - Target notifications to specific users/groups
 * - Set priorities and expiry dates
 * - View read/unread status
 * - Bulk operations
 * 
 * @author By John Bellew - Infinia.ie - last updated 22/07/2025
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faPlus,
    faEdit,
    faTrash,
    faRefresh,
    faFilter,
    faMagnifyingGlass,
    faSort,
    faSortUp,
    faSortDown,
    faEye,
    faEyeSlash,
    faCalendar,
    faClock,
    faUsers,
    faUser,
    faBuilding,
    faGlobe,
    faSpinner,
    faDownload,
    faUpload,
    faBullhorn,
    faNewspaper,
    faInfoCircle,
    faExclamationTriangle,
    faSync
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import apiHelper from '../../utils/apiHelper';
import NotificationForm from './NotificationForm';
import './NotificationManagement.css';

function NotificationManagement({ currentUser }) {
    // Main data states
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Form states
    const [showForm, setShowForm] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [viewMode, setViewMode] = useState(false);

    // Filter and search states
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [targetFilter, setTargetFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Sorting states
    const [sortField, setSortField] = useState('Created');
    const [sortDirection, setSortDirection] = useState('desc');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);

    // Selection states for bulk operations
    const [selectedItems, setSelectedItems] = useState([]);

    // Load notifications on component mount
    useEffect(() => {
        loadNotifications();
    }, []);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, typeFilter, priorityFilter, targetFilter, statusFilter]);

    /**
     * Load all notifications from SharePoint
     */
    const loadNotifications = async (forceFresh = false) => {
        setIsLoading(true);
        try {
            // Add cache-busting parameter for force refresh
            const url = forceFresh 
                ? `/api/graph/getAllNotifications?t=${Date.now()}` 
                : '/api/graph/getAllNotifications';
                
            const response = await apiHelper.get(url);
            
            if (response?.data?.notifications) {
                setNotifications(response.data.notifications);
            } else {
                setNotifications([]);
            }
        } catch (error) {
            toast.error('Failed to load notifications');
            setNotifications([]);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Refresh notifications with loading indicator
     */
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await loadNotifications(true); // Force fresh data
            toast.success('Notifications refreshed');
        } catch (error) {
            toast.error('Failed to refresh notifications');
        } finally {
            setIsRefreshing(false);
        }
    };

    /**
     * Handle creating a new notification
     */
    const handleNewNotification = () => {
        setSelectedNotification(null);
        setShowForm(true);
        setViewMode(false);
    };

    /**
     * Handle editing an existing notification
     */
    const handleEditNotification = (notification) => {
        setSelectedNotification(notification);
        setShowForm(true);
        setViewMode(false);
    };

    /**
     * Handle viewing an existing notification (with read status)
     */
    const handleViewNotification = (notification) => {
        setSelectedNotification(notification);
        setShowForm(true);
        setViewMode(true);
    };

    /**
     * Handle form submission (create/update)
     */
    const handleFormSave = async (notificationData) => {
        setIsFormLoading(true);
        try {
            if (selectedNotification) {
                // Update existing notification
                const response = await apiHelper.put(`/api/graph/updateNotification/${selectedNotification.id}`, notificationData);
                toast.success('Notification updated successfully');
            } else {
                // Create new notification
                await apiHelper.post('/api/graph/createNotification', notificationData);
                toast.success('Notification created successfully');
            }
            
            // Force close form and clear all modal state
            setShowForm(false);
            setSelectedNotification(null);
            setViewMode(false);
            
            // Force refresh the notifications list with small delay for SharePoint processing
            setTimeout(async () => {
                await loadNotifications(true); // Force fresh data with cache busting
            }, 200);
        } catch (error) {
            toast.error(selectedNotification ? 'Failed to update notification' : 'Failed to create notification');
        } finally {
            setIsFormLoading(false);
        }
    };

    /**
     * Handle form close
     */
    const handleFormClose = () => {
        setShowForm(false);
        setSelectedNotification(null);
        setViewMode(false);
    };

    /**
     * Handle deleting a notification
     */
    const handleDeleteNotification = async (notification, event) => {
        event.stopPropagation();
        
        if (!window.confirm(`Are you sure you want to delete the notification "${notification.title}"?`)) {
            return;
        }

        try {
            await apiHelper.delete(`/api/graph/deleteNotification/${notification.id}`);
            toast.success('Notification deleted successfully');
            await loadNotifications(); // Refresh list
        } catch (error) {
            toast.error('Failed to delete notification');
        }
    };

    /**
     * Handle sorting
     */
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    /**
     * Get sort indicator component
     */
    const SortIndicator = ({ field }) => {
        if (sortField !== field) {
            return <FontAwesomeIcon icon={faSort} className="sort-icon" />;
        }
        return (
            <FontAwesomeIcon 
                icon={sortDirection === 'asc' ? faSortUp : faSortDown} 
                className="sort-icon active" 
            />
        );
    };

    /**
     * Filter and sort notifications
     */
    const getFilteredNotifications = () => {
        let filtered = notifications.filter(notification => {
            // Search filter
            const matchesSearch = !searchTerm || 
                notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notification.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notification.targetValue?.toLowerCase().includes(searchTerm.toLowerCase());

            // Type filter
            const matchesType = typeFilter === 'all' || notification.type === typeFilter;

            // Priority filter
            const matchesPriority = priorityFilter === 'all' || notification.priority === priorityFilter;

            // Target filter
            const matchesTarget = targetFilter === 'all' || notification.targetType === targetFilter;

            // Status filter (active/expired)
            let matchesStatus = true;
            if (statusFilter === 'active') {
                const now = new Date();
                const expiryDate = notification.expiryDate ? new Date(notification.expiryDate) : null;
                matchesStatus = !expiryDate || expiryDate > now;
            } else if (statusFilter === 'expired') {
                const now = new Date();
                const expiryDate = notification.expiryDate ? new Date(notification.expiryDate) : null;
                matchesStatus = expiryDate && expiryDate <= now;
            }

            return matchesSearch && matchesType && matchesPriority && matchesTarget && matchesStatus;
        });

        // Sort
        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            // Handle dates
            if (sortField === 'Created' || sortField === 'expiryDate') {
                aValue = aValue ? new Date(aValue) : new Date(0);
                bValue = bValue ? new Date(bValue) : new Date(0);
            }

            // Handle strings
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue || '').toLowerCase();
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    };

    /**
     * Get paginated notifications
     */
    const getPaginatedNotifications = () => {
        const filtered = getFilteredNotifications();
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filtered.slice(startIndex, startIndex + itemsPerPage);
    };

    /**
     * Get notification type icon
     */
    const getTypeIcon = (type) => {
        switch (type) {
            case 'News': return faNewspaper;
            case 'Event': return faCalendar;
            case 'Survey': return faUsers;
            case 'Alert': return faExclamationTriangle;
            case 'Update': return faSync;
            default: return faInfoCircle;
        }
    };

    /**
     * Get target type icon
     */
    const getTargetIcon = (targetType) => {
        switch (targetType) {
            case 'All': return faGlobe;
            case 'Individual': return faUser;
            case 'WorkingGroup': return faUsers;
            case 'Company': return faBuilding;
            default: return faUser;
        }
    };

    /**
     * Format date for display
     */
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    /**
     * Check if notification is expired
     */
    const isExpired = (expiryDate) => {
        if (!expiryDate) return false;
        return new Date(expiryDate) < new Date();
    };

    /**
     * Get notification status badge
     */
    const getStatusBadge = (notification) => {
        if (isExpired(notification.expiryDate)) {
            return <span className="status-badge expired">Expired</span>;
        }
        return <span className="status-badge active">Active</span>;
    };

    /**
     * Get priority badge
     */
    const getPriorityBadge = (priority) => {
        return <span className={`priority-badge priority-${priority?.toLowerCase()}`}>{priority}</span>;
    };

    const filteredNotifications = getFilteredNotifications();
    const paginatedNotifications = getPaginatedNotifications();
    const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

    return (
        <div className="notification-management">
            {/* Header */}
            <div className="management-header">
                <div className="header-left">
                    
                    <span className="item-count">
                        {filteredNotifications.length} notifications
                    </span>
                </div>
                <div className="header-actions">
                    <button 
                        className="btn btn-secondary"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        title="Refresh notifications"
                    >
                        <FontAwesomeIcon icon={faRefresh} spin={isRefreshing} />
                        Refresh
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={handleNewNotification}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New Notification
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="filter-row">
                    <div className="notification-search-container">
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="notification-search-icon" />
                        <input
                            type="text"
                            placeholder="Search notifications..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="notification-search-input"
                        />
                    </div>
                    
                    <select 
                        value={typeFilter} 
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Types</option>
                        <option value="News">News</option>
                        <option value="Event">Event</option>
                        <option value="Survey">Survey</option>
                        <option value="Alert">Alert</option>
                        <option value="Update">Update</option>
                        <option value="Info">Info</option>
                    </select>

                    <select 
                        value={priorityFilter} 
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Priorities</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                    </select>

                    <select 
                        value={targetFilter} 
                        onChange={(e) => setTargetFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Targets</option>
                        <option value="All">Everyone</option>
                        <option value="Individual">Individual</option>
                        <option value="WorkingGroup">Working Group</option>
                        <option value="Company">Company</option>
                    </select>

                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                    </select>
                </div>
            </div>

            {/* Notifications Table */}
            <div className="notifications-table-container">
                {isLoading ? (
                    <div className="loading-state">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Loading notifications...</span>
                    </div>
                ) : paginatedNotifications.length === 0 ? (
                    <div className="empty-state">
                        <FontAwesomeIcon icon={faBell} />
                        <h4>No notifications found</h4>
                        <p>
                            {searchTerm || typeFilter !== 'all' || priorityFilter !== 'all' || targetFilter !== 'all' || statusFilter !== 'all'
                                ? 'No notifications match your current filters.'
                                : 'No notifications have been created yet.'
                            }
                        </p>
                        {(!searchTerm && typeFilter === 'all' && priorityFilter === 'all' && targetFilter === 'all' && statusFilter === 'all') && (
                            <button className="btn btn-primary" onClick={handleNewNotification}>
                                <FontAwesomeIcon icon={faPlus} />
                                Create First Notification
                            </button>
                        )}
                    </div>
                ) : (
                    <table className="notifications-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('title')} className="sortable">
                                    Title <SortIndicator field="title" />
                                </th>
                                <th onClick={() => handleSort('type')} className="sortable">
                                    Type <SortIndicator field="type" />
                                </th>
                                <th onClick={() => handleSort('priority')} className="sortable">
                                    Priority <SortIndicator field="priority" />
                                </th>
                                <th onClick={() => handleSort('targetType')} className="sortable">
                                    Target <SortIndicator field="targetType" />
                                </th>
                                <th onClick={() => handleSort('Created')} className="sortable">
                                    Created <SortIndicator field="Created" />
                                </th>
                                <th onClick={() => handleSort('expiryDate')} className="sortable">
                                    Expires <SortIndicator field="expiryDate" />
                                </th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedNotifications.map((notification) => (
                                <tr 
                                    key={notification.id}
                                    className={`notification-row ${isExpired(notification.expiryDate) ? 'expired' : ''}`}
                                    onClick={() => handleViewNotification(notification)}
                                >
                                    <td className="title-cell">
                                        <div className="title-content">
                                            <FontAwesomeIcon 
                                                icon={getTypeIcon(notification.type)} 
                                                className="type-icon"
                                            />
                                            <div>
                                                <div className="notification-title">{notification.title}</div>
                                                {/* <div className="notification-preview">
                                                    {notification.message?.replace(/<[^>]*>/g, '').substring(0, 80)}...
                                                </div> */}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{notification.type}</td>
                                    <td>{getPriorityBadge(notification.priority)}</td>
                                    <td>
                                        <div className="target-info">
                                            <FontAwesomeIcon 
                                                icon={getTargetIcon(notification.targetType)} 
                                                className="target-icon"
                                            />
                                            <span>
                                                {notification.targetType}
                                                {notification.targetValue && ` (${notification.targetValue})`}
                                            </span>
                                        </div>
                                    </td>
                                    <td>{formatDate(notification.created)}</td>
                                    <td>{formatDate(notification.expiryDate)}</td>
                                    <td>{getStatusBadge(notification)}</td>
                                    <td className="actions-cell">
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditNotification(notification);
                                            }}
                                            title="Edit notification"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={(e) => handleDeleteNotification(notification, e)}
                                            title="Delete notification"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="pagination">
                    <button 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="btn btn-secondary"
                    >
                        Previous
                    </button>
                    <span className="page-info">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="btn btn-secondary"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Notification Form Modal */}
            {showForm && (
                <NotificationForm
                    notification={selectedNotification}
                    onSave={handleFormSave}
                    onClose={handleFormClose}
                    isLoading={isFormLoading}
                    currentUser={currentUser}
                    viewMode={viewMode}
                />
            )}
        </div>
    );
}

export default NotificationManagement; 